import React, { useState } from "react";
import {
  TextField,
  Box,
  OutlinedInput,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
} from "@mui/material";
import { ClassData } from "../../hooks/useClasses";
import { Teacher } from "../../hooks/useTeacher";
import { Student } from "../../hooks/useStudents";
import { SelectChangeEvent } from "@mui/material/Select";
import { ClassOptions } from "../enum/ClassOptions";
import { useSessionClass } from "contexts/SessionClassContext";
import { useTranslation } from "react-i18next";
import { useError } from "../../contexts/ErrorContext";
import CommonModal from "../common/CommonModal";

interface ClassFormModalProps {
  open: boolean;
  handleClose: () => void;
  initialData?: ClassData;
  onSubmit: (formData: ClassData) => void;
  isEdit: boolean;
  schoolId: number;
  teachers: Teacher[];
  students: Student[];
  existingClasses?: ClassData[];
}

const ClassFormModal: React.FC<ClassFormModalProps> = ({
  open,
  handleClose,
  initialData,
  onSubmit,
  isEdit,
  schoolId,
  teachers,
  students,
  existingClasses = [],
}) => {
  const initialFormState: ClassData = {
    name: "",
    section: "",
    schoolId: schoolId,
  };

  const [formData, setFormData] = useState<ClassData>(initialFormState);
  const { selectedSession } = useSessionClass();
  const { t } = useTranslation();
  const { showError } = useError();

  React.useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFormData({ ...initialData, schoolId });
      } else {
        setFormData({ ...initialFormState, schoolId });
      }
    }
    // eslint-disable-next-line
  }, [open, initialData, isEdit, schoolId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "section") {
      const cleaned = value.replace(/\s+/g, "");
      setFormData((prev) => ({ ...prev, [name]: cleaned.toUpperCase() }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (!selectedSession || !selectedSession.id) {
      showError(t("please_select_a_session"));
      return;
    }

    if (isEdit && initialData?.id) {
      const duplicateClass = existingClasses.find(
        (cls) =>
          cls.id !== initialData.id &&
          cls.name === formData.name &&
          cls.section === formData.section &&
          cls.schoolId === schoolId
      );
      if (duplicateClass) {
        showError(`A class already exists with class : ${formData.name} and section : ${formData.section}`);
        return;
      }
    }

    onSubmit({ ...formData });
    if (!isEdit) {
      setFormData({ ...initialFormState, schoolId });
    }
  };

  const isFormValid =
    formData.name &&
    formData.name.trim() !== "" &&
    formData.section &&
    formData.section.trim() !== "" &&
    selectedSession &&
    selectedSession.id;

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      title={isEdit ? t("edit_class") : t("add_new_class")}
      maxWidth="sm"
      onSubmit={handleSubmit}
      submitLabel={isEdit ? t("update") : t("create")}
      submitDisabled={!isFormValid}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControl fullWidth margin="dense" required variant="outlined" size="small">
          <InputLabel sx={{ "& .MuiFormLabel-asterisk": { color: "red" } }}>
            {t("class_name")}
          </InputLabel>
          <Select
            name="name"
            value={formData.name}
            onChange={handleSelectChange}
            label={t("class_name")}
            size="small"
            required
            input={<OutlinedInput label={t("class_name")} size="small" />}
            displayEmpty
            sx={{
              minHeight: "40px",
              display: "flex",
              alignItems: "center",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                paddingTop: "8px",
                paddingBottom: "8px",
              },
              border: "1px solid #ccc",
              borderRadius: "8px",
            }}
            renderValue={(selected) =>
              !selected ? (
                <span style={{ color: "#aaa" }}></span>
              ) : (
                <span style={{ color: "black" }}>{selected}</span>
              )
            }
            MenuProps={{
              PaperProps: { style: { maxHeight: 300 } },
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
            }}
          >
            {Object.values(ClassOptions).map((classLabel) => (
              <MenuItem key={classLabel} value={classLabel}>
                {classLabel}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          name="section"
          label={t("section")}
          value={formData.section}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === " ") e.preventDefault();
          }}
          fullWidth
          margin="dense"
          required
          size="small"
          variant="outlined"
          sx={{
            "& .MuiFormLabel-asterisk": { color: "red" },
            "& .MuiOutlinedInput-root": {
              border: "1px solid #ccc",
              borderRadius: "8px",
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#3092e3" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3092e3" },
            },
          }}
        />
      </Box>
    </CommonModal>
  );
};

export default ClassFormModal;
